/**
 * PipelineCreateModal — criar um funil escolhendo um template (ou vazio).
 *
 * Substitui o window.prompt() antigo. Os templates vêm do backend
 * (/crm/pipeline-templates — fonte central em api/crm/templates.py), então
 * adicionar templates novos não exige mudança aqui.
 *
 * Visual: mesmo padrão de modal centrado do módulo Financeiro (fc-modal-*,
 * ../financeiro/contas.css) + cartão de template (pm-tpl-*, ./pipelines.css).
 *
 * Props:
 *   onClose()   — fecha sem criar
 *   onCreated(p)— criou o funil (recebe o pipeline criado; o board seleciona-o)
 */

import { useEffect, useState } from 'react'
import Icon from '../../components/icons.jsx'
import { getPipelineTemplates, createPipeline } from '../../db/api.js'
import '../financeiro/contas.css'
import './pipelines.css'

export default function PipelineCreateModal({ onClose, onCreated }) {
  const [nome, setNome] = useState('')
  const [templates, setTemplates] = useState([])
  const [template, setTemplate] = useState('vazio')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    let alive = true
    getPipelineTemplates()
      .then((ts) => { if (alive) setTemplates(ts) })
      .catch(() => { /* sem templates: só a opção vazio (fallback abaixo) */ })
    return () => { alive = false }
  }, [])

  // fallback garante ao menos "vazio" caso a lista não carregue.
  const opts = templates.length ? templates : [
    { key: 'vazio', label: 'Pipeline vazio', descricao: 'Comece do zero e crie suas próprias etapas.', stages: [] },
  ]

  const create = async () => {
    if (saving) return
    if (!nome.trim()) { setErr('Nome é obrigatório.'); return }
    setSaving(true); setErr(null)
    try {
      const p = await createPipeline({ nome: nome.trim(), template })
      onCreated(p)
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  return (
    <div className="fc-modal-overlay" onMouseDown={onClose}>
      <div className="fc-modal" style={{ width: 480 }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="fc-modal-head">
          <span className="fc-drawer-title">Novo pipeline</span>
          <button className="fc-drawer-x" onClick={onClose}><Icon n="x" s={16} sw={2} /></button>
        </div>
        <div className="fc-modal-body">
          {err && <div className="fc-error">{err}</div>}

          <div className="fc-field">
            <label className="fc-label">Nome do pipeline<i>*</i></label>
            <input
              className="fc-input" value={nome} autoFocus
              onChange={(e) => setNome(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') create() }}
              placeholder="Ex.: Vendas SP"
            />
          </div>

          <div className="fc-field">
            <label className="fc-label">Modelo</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {opts.map((t) => {
                const active = template === t.key
                return (
                  <button
                    key={t.key}
                    type="button"
                    className={`pm-tpl-card${active ? ' active' : ''}`}
                    onClick={() => setTemplate(t.key)}
                  >
                    <span className="pm-tpl-radio">{active && <span className="pm-tpl-radio-dot" />}</span>
                    <span style={{ minWidth: 0 }}>
                      <span className="pm-tpl-label">{t.label}</span>
                      {t.descricao && <span className="pm-tpl-desc">{t.descricao}</span>}
                      {!!(t.stages && t.stages.length) && (
                        <span className="pm-tpl-meta">{t.stages.join(' · ')}</span>
                      )}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
        <div className="fc-modal-foot">
          <button className="fc-ctrl" onClick={onClose}>Cancelar</button>
          <button className="fc-ctrl primary" onClick={create} disabled={saving}>
            {saving ? 'Criando…' : 'Criar'}
          </button>
        </div>
      </div>
    </div>
  )
}
